import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { Product, ProductQueryParams } from '@/types/commerce'
import { objectToParamsQuery } from '@/utils'

import settings from '@/settings/public'

interface UseProductsProps {
  params?: ProductQueryParams
  options?: Omit<UseQueryOptions<Product[], Error>, 'queryKey' | 'queryFn'>
  filterProducts?: FilterProducts
}

type FilterProducts =
  | string[]
  | {
      includeIds?: string[]
    }

const NO_PLANS_CODE = 42212

const applyProductsFilter = (
  products: Product[],
  filterProducts?: FilterProducts
): Product[] => {
  if (!filterProducts) return products

  const idsToExclude = Array.isArray(filterProducts)
    ? filterProducts
    : filterProducts.includeIds ?? []

  return products.filter(product => !idsToExclude.includes(product._id))
}

const fetchProducts = async (args?: UseProductsProps): Promise<Product[]> => {
  const { params } = args || {}
  const paramQueryString = objectToParamsQuery(params || {})

  const url = new URL(`${settings.api.host}/api/1.0/commerce/products/availables`)
  const searchParams = new URLSearchParams()

  if (paramQueryString) {
    const paramParams = new URLSearchParams(paramQueryString);
    for (const [key, value] of paramParams) {
      searchParams.append(key, value);
    }
  }
  url.search = searchParams.toString()

  const response = await fetch(url.toString())

  const productsJson = await response.json()

  if (!response.ok) {
    if (productsJson?.error?.code === NO_PLANS_CODE) {
      return []
    }

    throw new Error(`Failed to fetch products: ${response.status}`)
  }

  if (!productsJson.data || !Array.isArray(productsJson.data.items)) {
    throw new Error('Invalid data format')
  }

  return productsJson.data.items
}

export const useProducts = ({ params, options, filterProducts }: UseProductsProps = {}) => {
  const productsQuery = useQuery({
    queryKey: ['products', params],
    queryFn: () => fetchProducts({ params }),
    staleTime: 60_000,
    ...options
  })

  const filteredProducts = productsQuery.data
    ? applyProductsFilter(productsQuery.data, filterProducts)
    : productsQuery.data

  return {
    products: productsQuery.data,
    filteredProducts,
    ...productsQuery
  }
}
